import { request, Player } from "./client";
import { registerMessages } from "./errors";

registerMessages({
  "failed to list parents": "Не удалось загрузить родителей",
  "name, login and password are required": "Укажите имя, логин и пароль",
  "phone or email is required": "Укажите телефон или почту",
  // The server answers a duplicate login with this same bare 500.
  "failed to create parent": "Не удалось добавить родителя. Проверьте, не занят ли логин",
  "parent not found": "Родитель не найден",
  "failed to update parent": "Не удалось сохранить родителя",
  "failed to delete parent": "Не удалось удалить родителя",
  "login and password are required": "Не указаны логин и пароль",
  "failed to update credentials": "Не удалось сменить логин и пароль. Проверьте, не занят ли логин",
  "failed to list children": "Не удалось загрузить детей родителя",
  "player_id is required": "Не выбран ребёнок",
  "failed to link child": "Не удалось привязать ребёнка",
  "failed to unlink child": "Не удалось отвязать ребёнка",
});

export interface Parent {
  id: string;
  name: string;
  login: string;
  password?: string;
  phone: string | null;
  email: string | null;
  is_active: boolean;
  // Omitted by the server when the parent has no children.
  children?: Player[];
  created_at: string;
  updated_at: string;
}

export interface ParentChildLink {
  id: string;
  parent_id: string;
  player_id: string;
  discount_percent: number;
}

export interface Credentials {
  login: string;
  password: string;
}

const path = (id: string) => `/api/parents/${encodeURIComponent(id)}`;

export const parentsApi = {
  list: () => request<Parent[] | null>("/api/parents/"),

  create: (input: { name: string; login: string; password: string; phone: string | null; email: string | null }) =>
    request<Parent>("/api/parents/", { method: "POST", body: JSON.stringify(input) }),

  // On this route a null phone or email means "keep the old one", so a cleared
  // field has to go as "" — the server stores that as empty.
  update: (id: string, changes: { name: string; phone: string; email: string }) =>
    request<Parent>(path(id), { method: "PUT", body: JSON.stringify(changes) }),

  // Soft delete: the parent can't log in, their children links are dropped and
  // orphaned children's unpaid invoices are cancelled. The row stays.
  remove: (id: string) => request<{ ok: boolean }>(path(id), { method: "DELETE" }),

  // Brings back only the account: links dropped on delete are not restored.
  restore: (id: string) =>
    request<Parent>(path(id), { method: "PUT", body: JSON.stringify({ is_active: true }) }),

  // The server takes the new pair as given and ends the parent's sessions.
  resetCredentials: (id: string, credentials: Credentials) =>
    request<Parent>(`${path(id)}/reset-credentials`, { method: "POST", body: JSON.stringify(credentials) }),

  listChildren: (id: string) => request<Player[] | null>(`${path(id)}/children`),

  linkChild: (id: string, playerID: string, discountPercent: number) =>
    request<ParentChildLink>(`${path(id)}/children`, {
      method: "POST",
      body: JSON.stringify({ player_id: playerID, discount_percent: discountPercent }),
    }),

  unlinkChild: (id: string, playerID: string) =>
    request<{ ok: boolean }>(`${path(id)}/children/${encodeURIComponent(playerID)}`, { method: "DELETE" }),
};

// Same alphabet and shape as the mobile app (admin_parents_page.dart): no 0/o
// or 1/l, so a parent can retype them from a message without guessing.
const ALPHABET = "abcdefghijkmnpqrstuvwxyz23456789";

function secureRandom(max: number): number {
  // jsdom in tests has no Web Crypto; every browser the panel runs in does.
  if (!window.crypto?.getRandomValues) return Math.floor(Math.random() * max);
  const values = new Uint32Array(1);
  window.crypto.getRandomValues(values);
  return values[0] % max;
}

export function generateParentCredentials(
  taken: ReadonlySet<string>,
  random: (max: number) => number = secureRandom
): Credentials {
  const pick = (length: number) =>
    Array.from({ length }, () => ALPHABET[random(ALPHABET.length)]).join("");
  let login = `parent_${pick(4)}`;
  // A login another parent has fails on the server with a bare 500, so draw again.
  for (let tries = 0; taken.has(login) && tries < 20; tries++) {
    login = `parent_${pick(4)}`;
  }
  return { login, password: pick(8) };
}
